import React, { useState } from 'react';
import client from '../api/client';

export const ContactPage: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'A valid email address is required.';
    if (!form.message.trim()) e.message = 'Message is required.';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsLoading(true);
    client.post('/contact', form)
      .then(() => {
        setSubmitted(true);
        setForm({ name: '', email: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      })
      .catch(() => {
        setErrors({ message: 'Failed to send message. Please try emailing admin@rdf.global directly.' });
      })
      .finally(() => setIsLoading(false));
  };

  const field = (id: keyof typeof form) => ({
    value: form[id],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm(f => ({ ...f, [id]: e.target.value }));
      setErrors(er => ({ ...er, [id]: '' }));
    },
  });

  return (
    <div className="page">
      {/* Hero Header */}
      <section
        className="section--sm"
        style={{
          background: 'linear-gradient(135deg, var(--c-primary) 0%, var(--c-primary-dark) 100%)',
          paddingTop: 60,
          paddingBottom: 60,
        }}
      >
        <div className="container">
          <span className="section-label" style={{color: 'rgba(255,255,255,.65)'}}>
            Get in Touch
          </span>
          <h1 style={{color: 'white', marginBottom: 12}}>Contact the RDF</h1>
          <p style={{color: 'rgba(255,255,255,.75)', maxWidth: 520, fontSize: 16}}>
            Have questions about membership, regulatory guidance, or RDF activities? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section">
        <div className="container">
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'start'}}>
            {/* Form */}
            <div>
              <h2 style={{marginBottom: 8}}>Send us a message</h2>
              <p style={{color: 'var(--c-text-muted)', marginBottom: 28}}>
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              {submitted && <div className="alert alert-success" style={{marginBottom: 20}}>Thank you for your message. We'll be in touch soon!</div>}

              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label className="form-label">Full Name <span className="req">*</span></label>
                  <input
                    className={`form-input ${errors.name ? 'is-error' : ''}`}
                    type="text"
                    {...field('name')}
                    placeholder="Your name"
                  />
                  {errors.name && <div className="form-error">{errors.name}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address <span className="req">*</span></label>
                  <input
                    className={`form-input ${errors.email ? 'is-error' : ''}`}
                    type="email"
                    {...field('email')}
                    placeholder="your@email.com"
                  />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Message <span className="req">*</span></label>
                  <textarea
                    className={`form-input ${errors.message ? 'is-error' : ''}`}
                    {...field('message')}
                    placeholder="Your message..."
                    rows={6}
                  />
                  {errors.message && <div className="form-error">{errors.message}</div>}
                </div>

                <button className="btn btn-primary" type="submit" disabled={isLoading}>
                  {isLoading ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 style={{marginBottom: 8}}>Our details</h2>
              <p style={{color: 'var(--c-text-muted)', marginBottom: 28}}>
                Alternatively, reach out to us directly using the contact information below.
              </p>

              <div style={{display: 'grid', gap: 20}}>
                <div className="card" style={{padding: '24px 20px'}}>
                  <h3 style={{fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--c-primary)'}}>Email</h3>
                  <a href="mailto:admin@rdf.global" style={{color: 'var(--c-primary)', textDecoration: 'none', fontSize: 15}}>
                    admin@rdf.global
                  </a>
                  <p style={{color: 'var(--c-text-muted)', fontSize: 13, marginTop: 4}}>
                    For general inquiries and membership questions.
                  </p>
                </div>

                <div className="card" style={{padding: '24px 20px'}}>
                  <h3 style={{fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--c-primary)'}}>Website</h3>
                  <a href="https://rdf.global" target="_blank" rel="noreferrer" style={{color: 'var(--c-primary)', textDecoration: 'none', fontSize: 15}}>
                    rdf.global
                  </a>
                  <p style={{color: 'var(--c-text-muted)', fontSize: 13, marginTop: 4}}>
                    Visit our main website for more information.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
